/**
 * 素材style封装
 *
 * @format
 */

import { cloneDeep, mergeWith } from 'lodash';
import { isArrayProperty } from '@/utils/is-array-property';

interface Option {
  [key: string]: any;
}

export const ColorList = ['#33ADE4', '#99C24B', '#F17446', '#FF9848', '#9E61F3'];

export default class EChartOptions {
  private option: Option = {
    color: ColorList,
    title: {
      text: '图表标题',
      left: 'auto',
      textStyle: {
        color: '#333',
        fontSize: 18,
        fontWeight: 'normal'
      }
    },
    textStyle: {
      color: '#c5c5c5' /**调亮文字颜色 */
    },
    legend: {
      /** 图例图标大小 */
      itemWidth: 10,
      itemHeight: 10,
      icon: 'circle',
      align: 'left',
      textStyle: {
        color: '#c5c5c5' /**调亮图例文字颜色 */
      }
    },
    grid: {
      containLabel: true,
      left: 100,
      right: 100,
      top: 60,
      bottom: 60
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      confine: true
    }
  };

  /**
   * 更新标题模块
   * @param map title map
   */
  public setTitle(map: Option) {
    this.option.title = mergeWith(this.option.title, map, isArrayProperty);
  }

  /**
   * 更新颜色
   * @param color
   */
  public setColor(color: string[]) {
    this.option.color = color;
  }

  /**
   * 更新图例
   * @param map
   */
  public setLegend(map: Option) {
    this.option.legend = mergeWith(this.option.legend, map, isArrayProperty);
  }

  /**
   * 更新图例位置
   * @param position  left top right bottom
   */
  public setLegendPosition(position?: string) {
    const option: any = {
      left: undefined,
      right: undefined,
      bottom: undefined,
      orient: 'horizontal'
    };
    switch (position) {
      case 'top':
        option.left = 'right';
        option.top = 'top';
        option.right = undefined;
        option.bottom = undefined;
        break;
      case 'bottom':
        option.left = 'center';
        option.top = 'bottom';
        break;
      case 'left':
        option.left = 'left';
        option.top = 'middle';
        option.orient = 'vertical';
        break;
      case 'right':
        option.left = 'right';
        option.top = 'middle';
        option.orient = 'vertical';
        break;
    }
    this.option.legend = mergeWith(this.option.legend, option, isArrayProperty);
  }

  /**
   * 设置边距
   * @param map
   */
  public setGrid(map: Option) {
    this.option.grid = mergeWith(this.option.grid, map, isArrayProperty);
  }

  public setTooltip(map: Option, replace = false) {
    if (replace) {
      this.option.tooltip = map;
    } else {
      this.option.tooltip = mergeWith(this.option.tooltip, map, isArrayProperty);
    }
  }

  public setVisualMap(map: Option | null) {
    this.option.visualMap = map;
  }

  public setGeo(map: Option) {
    this.option.geo = map;
  }

  /**
   * 设置数据集
   * @param map
   */
  public setDataset(map: { sourceHeader?: any; source?: any[]; dimensions?: any[] }) {
    const { sourceHeader = [], dimensions = [], source = [] } = map;
    this.option.dataset = {
      sourceHeader,
      dimensions,
      source
    };
  }

  public setXAxis(map: Option, replace = false) {
    if (replace) {
      this.option.xAxis = map;
    } else {
      this.option.xAxis = mergeWith(this.option.xAxis, map, isArrayProperty);
    }
  }

  public setYAxis(map: Option | any[], replace = false) {
    if (replace) {
      this.option.yAxis = map;
    } else {
      this.option.yAxis = mergeWith(this.option.yAxis, map, isArrayProperty);
    }
  }

  public setSeries(series: any[]) {
    this.option.series = series;
  }

  public setOption(key: string, option: Option) {
    this.option[key] = option;
  }

  public setGraphic(graphic: any) {
    this.option.graphic = graphic;
  }

  public toJson() {
    return cloneDeep(this.option);
  }
}
